var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { createAsyncThunk, nanoid } from '@reduxjs/toolkit';
import { MathUtils } from '@printu/geom';
import { EditorActions } from 'app/stores/editor/actions';
import { Prompt } from 'app/stores/editor/prompt';
import { fileStorage } from 'app/stores/image/actions/storage';
import { loadImage } from 'app/stores/image/actions/utils';
import { shouldPromptUserIfPastingScreenshot } from 'app/utils/dnd';
import { snapToPixel } from 'app/utils/math';
import { substitute } from 'app/utils/string';
const Consts = {
    maxImageSize: { width: 4096, height: 4096 },
};
export const parseImageData = createAsyncThunk('image/parseImageData', (params, { dispatch }) => __awaiter(void 0, void 0, void 0, function* () {
    var _a, _b;
    const { data, source, name, userFeedback } = params;
    getLogger('ImageParser', 'debug')(substitute('Load image data from {0}:{1}; size: {2}; name:{3}', source, (_b = (_a = data === null || data === void 0 ? void 0 : data.constructor) === null || _a === void 0 ? void 0 : _a.name) !== null && _b !== void 0 ? _b : 'n/a', data.size, name !== null && name !== void 0 ? name : 'n/a'));
    const uid = nanoid();
    fileStorage.setFile(uid, data);
    try {
        const busyParams = userFeedback !== null && userFeedback !== void 0 ? userFeedback : (name
            ? { message: `Reading image file '${name}'` }
            : { message: 'Reading image data' });
        dispatch(EditorActions.showBusy(busyParams));
        const image = yield loadImage(uid, data);
        dispatch(EditorActions.hideBusy());
        let imageResizeFactor = 1;
        // on mac we run some extra logic to check if user is pasting screenshot made with default screncapture application
        if (source === 'clipboard' &&
            shouldPromptUserIfPastingScreenshot(data)) {
            const { width: screenWidth, height: screenHeight } = window.screen;
            const { width: imageWidth, height: imageHeight } = image.size;
            // if image size is equal exactly @xDPR assume this is a full screen screenshot
            const pixelRatio = window.devicePixelRatio || 1;
            const imageSizeMatchesScreenInPixelRatio = screenWidth * pixelRatio === imageWidth &&
                screenHeight * pixelRatio === imageHeight;
            //
            if (!imageSizeMatchesScreenInPixelRatio) {
                const promptResult = yield dispatch(Prompt.show({
                    title: 'Pasting screenshot?',
                    content: 'Are you pasing a screenshot taken with default Mac screenshot tool',
                    variant: 'confirmation',
                })).unwrap();
                if (promptResult) {
                    imageResizeFactor = 1 / pixelRatio;
                }
            }
        }
        // NOTE: scaling is required when pasting a screenshot (it that case screen dpi is applied)
        let scaledSize = {
            width: snapToPixel(Math.max(0, image.size.width * imageResizeFactor)),
            height: snapToPixel(Math.max(0, image.size.height * imageResizeFactor)),
        };
        // if image exceeds limits scale it keeping ratio
        const exceedsLimit = scaledSize.width > Consts.maxImageSize.width ||
            scaledSize.height > Consts.maxImageSize.height;
        if (exceedsLimit) {
            scaledSize = MathUtils.getSizeFitting(scaledSize, Consts.maxImageSize)
                .rounded()
                .toObject();
        }
        return Object.assign(Object.assign({}, image), { size: scaledSize });
    }
    catch (err) {
        dispatch(EditorActions.hideBusy());
        fileStorage.removeFile(uid);
        getLogger('ImageParser', 'error')(err);
        throw err;
    }
}));
